<template>
  <div>
    <div id="container">
      <div id="sidebar">
        <div id="sidebar-header">
          <div id="sidebar-address-logo">
            <router-link to="/">
              <img src="../assets/afc-tnschoolfinder-01.jpg" id="afc-logo"/>
            </router-link>
            <div v-if="formattedAddress" id="address-wrapper">
              <div>{{ formattedAddress }} </div>
              <div class="bold underlined">
                <router-link to="/" id="change-address-link">Change address</router-link>
                </div>
            </div>
          </div>
          <div v-if="displaySidebarResults" id="sidebar-title">
            <span>{{ sidebarTitle }} </span>
          </div>
          <div v-else class="no-results-found">
            <div class="no-results-found-wrapper">
              <span class="no-results-found-text">{{ noResultsFoundText }}</span>
            </div>
            <a :href="getHelpEmailAddress()" target="_blank"><div class="website-button contact-us-button">CONTACT US</div></a>
          </div>
          <div v-if="displaySidebarResults" id="sidebar-filters">
            <div id="school-type-wrapper">
              <div id="school-type-selection">
                <span v-for="(type,i) in schoolTypes" :key="i" class="school-selection-option" :class="{'school-type-selected': selectedSchoolTypes.indexOf(type.label) !== -1}" @click="selectSchoolType(type.label)">{{ type.displayValue }}</span>
              </div>
            </div>
            <div id="filters-drilldown-wrapper">
              <div id="sort-wrapper">
                <span id="sort-dropdown" @click="toggleSortDropdown()">{{ selectedSortType }}<i class="arrow-down"/></span>
                <div v-if="displaySortDropdown" id="sort-options-wrapper">
                  <div v-for="(type,i) in sortTypes" :key="i" class="sort-type" @click="selectSortType(type)">
                    {{ type }}
                  </div>
                </div>
              </div>
              <div id="school-level-wrapper">
                <div id="school-level-option" v-for="(level,i) in schoolLevels" :key="i" @click="handleSchoolLevelClicked(level)">
                  <input type="checkbox" :name="level.label" :value="level.label" v-model="level.checked"><span class="school-level-options">{{ level.label }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="displaySidebarResults" id="sidebar-results">
          <div :id="school.id + '-tab'" v-for="(school,i) in filteredSchoolResults" :key="i" class="school-results-item" @click.prevent="toggleSelectedSchool(school.id)">
            <div class="school-results-item-left">
              <div class="item-wrapper">
                <div class="school-info-wrapper">
                  <div class="school-results-name">{{ school.Name }}</div>
                  <div class="school-results-element">{{ school.Address }}</div>
                  <div class="school-results-element school-distance proxima-bold">{{ school.distanceFromAddr }} Miles Away</div>
                  <div v-if="selectedSchoolId === school.id"  class="school-results-expanded">
                    <div v-for="(infoItem) in validInfoItems(school)" :key="i">
                      <span class="proxima-bold">{{infoItem}}: </span>
                      <span v-if="infoItem === 'Financial Aid'">
                        <span v-for="(item,i) in school[infoItem].split(',')" :key="i">
                          <span v-if="isEsa(item)">
                            <span v-tooltip.top="getFinAidTooltipText('esa')">
                              <a @click.stop :href="esaSrc" target="_blank"  class="hyperlink">
                              {{item}}
                              </a>
                            </span>
                          </span>
                          <span v-else-if="isMost(item)">
                            <span v-tooltip.top="getFinAidTooltipText('most')">
                              <a @click.stop :href="mostSrc" target="_blank"  class="hyperlink">
                              {{item}}
                              </a>
                            </span>
                          </span>
                          <span v-else>{{item}}</span>
                        </span>
                      </span>
                      <span v-else>
                        <span>{{school[infoItem]}}</span>
                      </span>
                    </div>
                  </div>
                </div>
                <div class="school-icon-wrapper" v-tooltip.top="getIconTooltipText(school)">
                  <img v-if="getSchoolIcon(school) != null" :src="getSchoolIcon(school)">
                  <div v-if="getSchoolIcon(school)" class="school-icon-label">{{ getSchoolIconLabel(school) }}</div>
                </div>
              </div>
              <div v-if="selectedSchoolId === school.id && displayWebsiteButton(school)" class="school-results-website-wrapper">
                <div @click.prevent="openLink(school.Website)" class="website-button">WEBSITE</div>
              </div>
              <div v-if="selectedSchoolId === school.id && displayApplicationButton(school)" class="school-results-website-wrapper">
                <div @click.prevent="openLink(school['Application Link'])" class="website-button">APPLY NOW</div>
              </div>
              <div v-if="compareFeatureEnabled && selectedSchoolId === school.id && schoolCompareList.length < 3" class="compare-button-wrapper" @click="compareSchool(school)">
                <div class="compare-button-list">
                  COMPARE
                </div>
              </div>
            </div>
            <div class="school-results-item-right">
              {{ selectedSchoolId === school.id ? 'less': 'more'}}
            </div>
          </div>
        </div>
      </div>
      <div id="map">
        <Map :lat="lat" :long="long" :school-data="filteredSchoolResults" @pinClicked="handlePinClicked"/>
      </div>
    </div>
    <div id="compare-wrapper" v-if="schoolCompareList.length > 0">
      <div>
        <div v-for="(school,i) in schoolCompareList" :key="i" class="compare-item">
          <span>{{ school.Name }}</span>
          <span class="compare-item-number" @click="removeCompareItem(school)"><img :src="getDeleteIcon()" class="delete-icon"></span>
        </div>
        <div class="footer-button-wrapper">
          <div @click.prevent="openCompareModal()" class="website-button compare-button final-compare">
              <img src="../assets/afc-tnschoolfinder-03-trans.png" id="afc-logo-3"/>
            <span class="final-compare-text">COMPARE</span>
          </div>
        </div>
      </div>
    </div>
    <div id="compare-modal-wrapper" v-if="displayCompareModal">
      <div id="compare-modal-header">
        <span>Compare school options</span>
        <img :src="getDeleteIcon()" @click="closeCompareModal()" class="delete-icon compare-delete-icon">
      </div>
      <table id="compare-table">
        <tr id="compare-table-header">
          <th><!--This is intentionally blank--></th>
          <td v-for="(school,i) in schoolCompareList" :key="i" class="school-compare-header">{{ school.Name }}</td>
        </tr>
        <tr v-for="(category,i) in validInfoCategories" :key="i">

           <th v-if="displayCategory(category)">{{ category }}</th>
          <td v-if="isWebsite(category) && displayCategory(category)" v-for="(school,i) in schoolCompareList" :key="i">
            <a target="_blank" :href="getCompareItem(school, category)">{{ getCompareItem(school, category) }}</a>
          </td>
          
           <td v-if="!isWebsite(category) && displayCategory(category)" v-for="(school,i) in schoolCompareList" :key="i">
             {{ getCompareItem(school, category) }}
           </td>
        </tr>
        <tr v-if="displayWebsiteCompareRow">
          <th><!--This is intentionally blank--></th>
          <td v-for="(school,i) in schoolCompareList" :key="i" class="school-compare-website">
            <div v-if="displayWebsiteButton(school)" @click.prevent="openLink(school.Website)" class="website-button compare-website-button">WEBSITE</div>
            <div v-if="displayApplicationButton(school)" @click.prevent="openLink(school['Application Link'])" class="website-button compare-website-button">APPLY NOW</div>
          </td>
        </tr>
      </table>
    </div>
  </div>
</template>

<script>
import Map from '../components/Map.vue'
import GeneralMixin from '../mixins/general.js'
import SchoolData from '../../../../data/schools-processed.json'; // TODO: put this in S3

export default {
  name: 'ResultsPage',
  components: {
    Map
  },
  mixins: [GeneralMixin],
  data(){
    return {
      lat: 0,
      long: 0,
      formattedAddress: null,
      compareFeatureEnabled: false,
      sidebarTitle: 'School options in your area:',
      selectedSchoolTypes: [],
      schoolTypes: [
        {displayValue: "PUBLIC", label: "PUBLIC"},
        {displayValue: "PUBLIC CHARTER", label: "CHARTER"},
        {displayValue: "PRIVATE", label: "PRIVATE"}
      ],
      schoolLevels: [
        {label: "High school", checked: false},
        {label: "Middle school", checked: false},
        {label: "Elementary school", checked: false},
        {label: "Pre-K", checked: false}
      ],
      selectedSchoolLevels: [],
      schoolResults: [],
      sortTypes: ["Distance", "Alphabetical (A-Z)", "Alphabetical (Z-A)"],
      selectedSortType: "Distance",
      displaySortDropdown: false,
      selectedSchoolId: null,
      validInfoCategories: ["School Type", "Principal", "Website", "Phone", "Grades", "Tuition amount - lower school", "Tuition amount - upper school", "Financial Aid", "Dress code", "Transportation provided", "Before/After Care", "Religious Afiliation", "Success (% Proficient Overall)", "Test Administered", "Math (% Proficient)", "ELA (% Proficient)", "Algebra I (% Proficient)", "Algebra II (% Proficient)", "English I (% Proficient)", "English II (% Proficient)", "Graduation Rate (%)"],
      schoolCompareList: [],
      displayCompareModal: false,
      displaySidebarResults: true,
      noResultsFoundText: "We’re sorry, this information is only available in select cities. Contact us to have your city added.",
      mostSrc: "https://www.memphisscholarships.org/",
      esaSrc: "https://tnesa.org/eligibility/"
    }
  },
  mounted() {
    this.loadResults();
    this.enableCompareFeature();
  },
  computed: {
    filteredSchoolResults(){
      let results = JSON.parse(JSON.stringify(this.schoolResults)); // make a deep clone

      // filter based on selected school levels
      if(this.selectedSchoolLevels.length > 0){
        results = results.filter(school => {
          let validSchool = false;
          this.selectedSchoolLevels.forEach(level => {
            if (school.gradeLevels.indexOf(level) != -1) {
              validSchool = true;
            }
          });
          return validSchool;
        });
      }

      // filter based on selected school types
      if(this.selectedSchoolTypes.length > 0){
        results = results.filter(school => {
          let validSchool = false;
          this.selectedSchoolTypes.forEach(type => {
            if (school["School Type"].toUpperCase().indexOf(type) != -1) {
              validSchool = true;
            }
          });
          return validSchool;
          // return school["School Type"].toLowerCase() === this.selectedSchoolType.toLowerCase();
        });
      }

      // sort results based on selected sort type
      results.sort((a, b) => {
        if (this.selectedSortType === "Alphabetical (A-Z)"){
          return a.Name.localeCompare(b.Name);
        }
        if (this.selectedSortType === "Alphabetical (Z-A)"){
          return b.Name.localeCompare(a.Name);
        }
        return parseInt(a.distanceFromAddr) - parseInt(b.distanceFromAddr); // distance sort is default
      });

      return results;
    },
    displayWebsiteCompareRow(){
      let displayRow = false;
      this.schoolCompareList.forEach(school => {
        if(this.displayWebsiteButton(school) || this.displayApplicationButton(school)){
          displayRow = true;
        }
      });
      return displayRow;
    }
  },
  methods: {
    async loadResults(){
      const address = this.getUrlVars()["addr"];
      const zip = this.getUrlVars()["zip"];
      const geoCodingResult = await this.geocodeAddress(address, zip);
      this.lat = geoCodingResult.latLong.lat;
      this.long = geoCodingResult.latLong.lng;
      this.formattedAddress = geoCodingResult.formattedAddress;
      this.schoolResults = this.getClosestSchools(SchoolData, this.lat, this.long);

      /* Hides the sidebar results if no schools are located nearby. */
      if(this.schoolResults.length === 0){
        this.displaySidebarResults = false;
      }
    },
    selectSchoolType(selectedType){
      // console.log(selectedType, this.selectedSchoolTypes.indexOf(selectedType));
      // If the school type is already selected, clear the selected type
      if (this.selectedSchoolTypes.indexOf(selectedType) !== -1) {
        this.selectedSchoolTypes.splice(this.selectedSchoolTypes.indexOf(selectedType), 1);
      }
      else {
        this.selectedSchoolTypes.push(selectedType);
        this.scrollToTopOfResultsList();
      }
    },
    enableCompareFeature(){
      if(screen.width > 1000){
        this.compareFeatureEnabled = true;
      }
    },
    toggleSelectedSchool(index){
      const schoolId = this.selectedSchoolId === index ? null: index;

      this.$nextTick(() => {
        this.selectedSchoolId = schoolId
      });
      
      if (schoolId) {
        // trigger the popup
        document.getElementById(schoolId).click();
      } else {
        // close the popup
        document.getElementById(index).click();
      }
    },
    displayWebsiteButton(school){
      return school.Website && !school['Application Link'];
    },
    displayApplicationButton(school){
      return school['Application Link'];
    },
    getSchoolIcon(school){
      let icon = null;
      switch(school.icon){
        case 'Priority':
          icon = require('../assets/school-icon-3.svg');
          break;
        case 'Reward':
          icon = require('../assets/school-icon-1.svg');
          break;
        case 'Blue':
          icon = require('../assets/school-icon-4.svg');
          break;
        }
        return icon;
    },
    getSchoolIconLabel(school){
        let label = "";
        switch(school.icon){
            case 'Priority':
                label = 'Priority School';
                break;
            case 'Reward':
                label = 'Reward School';
                break;
            case 'Blue':
                label = 'Blue Ribbon School';
                break;
        }
        return label;
    },
    getFinAidTooltipText(type) {
      const tmpType = type.toLowerCase();
      let text = '';
      switch(tmpType) {
        case 'esa':
          text = 'The Tennessee Education Savings Account Pilot Program provides state-funded scholarships of more than $7,000 to eligible students in the Nashville and Memphis areas.';
          break
        case 'most':
          text = 'The Memphis Opportunity Scholarship Trust (MOST) provides scholarships for qualifying students in the Memphis area to attend private schools.'
        break
      }
      return text;
    },
    getEsaTooltipText() {
      return 'The Tennessee Education Savings Account Pilot Program provides state-funded scholarships of more than $7,000 to eligible students in the Nashville and Memphis areas.';
    },
    getMostTooltipText() {
      return 'The Memphis Opportunity Scholarship Trust (MOST) provides scholarships for qualifying students in the Memphis area to attend private schools.';
    },
    getIconTooltipText(school){
      let text = "";
      switch(school.icon){
        case 'Priority':
          text = "A school in need of improvement because it ranks among the bottom 5% in the state or has a graduation rate less than 67%.";
          break;
        case 'Reward':
          text = "A school that is improving in academic achievement and growth.";
          break;
        case 'Blue':
          text = "National award given to public or private schools recognized for overall academic excellence or progress in closing achievement gaps for traditionally underserved students.";
          break;
      }
      return text;
    },
    getCompareIcon(){
      return require('../assets/compare.svg');
    },
    getDeleteIcon(){
      return require('../assets/delete-icon.svg');
    },
    infoItemIsValid(school, item){
      return school[item] != null && school[item] != "--" && school[item] != "" && school[item] != "**" && school[item] != "*" && this.validInfoCategories.indexOf(item) != -1;
    },
    validInfoItems(school){
      const items = Object.keys(school);
      let result = [];
      items.forEach(item => {
        if(this.infoItemIsValid(school, item)) {
          result.push(item);
        }
      });
      return result;
    },
    isEsa(item) {
      return item.toLowerCase().includes('esa');
    },
    isMost(item) {
      return item.toLowerCase().includes('most');
    },
    handlePinClicked(id){
      this.selectedSchoolId = id;
      this.$nextTick(() => {
        const schoolInfoElement = document.getElementById(id + '-tab');
        schoolInfoElement.parentNode.scrollTop = schoolInfoElement.offsetTop - schoolInfoElement.parentNode.offsetTop; // scroll to the school in the list that was clicked
      });
    },
    handleSchoolLevelClicked(level){
      level.checked = !level.checked;
      const schoolLevel = level.label;
      if (this.selectedSchoolLevels.indexOf(schoolLevel) != -1) {
        // if the level is already selected, remove it
        this.selectedSchoolLevels.splice(this.selectedSchoolLevels.indexOf(schoolLevel), 1);
      } else {
        // if it's not selected, add it to the list
        this.selectedSchoolLevels.push(schoolLevel);
      }
      this.scrollToTopOfResultsList();
    },
    scrollToTopOfResultsList(){
      this.$nextTick(() => {
        const schoolInfoElement = document.getElementsByClassName('school-results-item')[0];
        if(schoolInfoElement) {
          schoolInfoElement.parentNode.scrollTop = schoolInfoElement.offsetTop - schoolInfoElement.parentNode.offsetTop; // scroll to the school in the list that was clicked
        }
      });
    },
    toggleSortDropdown(){
      this.displaySortDropdown = !this.displaySortDropdown;
    },
    selectSortType(type){
      this.selectedSortType = type;
      this.displaySortDropdown = false;
      this.scrollToTopOfResultsList();
    },
    openLink(link){
      window.open(link);
    },
    compareSchool(schoolToCompare){
      let schoolAlreadyExists = false;
      this.schoolCompareList.forEach(school => {
        if(schoolToCompare.id === school.id){
          schoolAlreadyExists = true;
        }
      });

      if(!schoolAlreadyExists){
        this.schoolCompareList.push(schoolToCompare);
      }
    },
    removeCompareItem(schoolToRemove){
      this.schoolCompareList = this.schoolCompareList.filter(school => { return school.id !== schoolToRemove.id });
    },
    openCompareModal(){
      this.displayCompareModal = true;
    },
    closeCompareModal(){
      this.displayCompareModal = false;
    },
    getCompareItem(school, item){
      return this.infoItemIsValid(school, item) ? school[item] : "";
    },
    displayCategory(category){
        let displayCategory = false;
        // the value exists and is valid
        this.schoolCompareList.forEach(school => {
            if(this.getCompareItem(school, category)) {
                displayCategory = true;
            }
        });
        return displayCategory;
    },
    isWebsite(category){
        let displayCategory = false;
        this.schoolCompareList.forEach(school => {
            if(this.getCompareItem(school, category) && category === 'Website') {
                displayCategory = true;
            }
        });
        return displayCategory;
    }
  }
}
</script>

<style scoped>
  @import "~vue-directive-tooltip/dist/vueDirectiveTooltip.css";
  @font-face {
    font-family: ProximaNovaSemiBold;
    src: url("../assets/proximanova-semibold.otf") format("opentype");
  }
  @font-face {
    font-family: ProximaNovaRegular;
    src: url("../assets/proxima-nova-regular.ttf") format("opentype");
  }
  a {
    color: inherit;
    text-decoration: none;
  }
  #container {
    display: flex;
    height: 100vh;
    overflow-y: hidden;
  }
  #sidebar {
    width: 35%;
  }
  #map {
    width: 70%;
    min-height: 80vh;
  }
  #sidebar-header {
    height: 300px;
  }
  #sidebar-results {
    height: calc(100% - 350px);
    overflow-y: scroll;
  }
  #sidebar-address-logo {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
    margin-top: 25px;
  }
  #afc-logo{
    height: 44px;
    width: 129px;
  }
  #afc-logo-3{
    height: 35px;
    width: 35px;
  }
  #address-wrapper{
    font-size: 12px;
    width: 150px;
    text-align: center;
  }
  .bold {
    font-weight: 600;
  }
  .underlined {
    text-decoration: underline;
  }

  #sidebar-title{
    width: 100%;
    height: 56px;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    color: #1f3561;
    background-color: #fafafa;
    margin-top: 5px;
    font-family: ProximaNovaSemiBold;
  }

  .school-selection-option{
          cursor: pointer;
    background-color: #a4d8f1;
    padding: 13px 12px 13px 12px;
  }

  .school-selection-option:first-child{
    border-top-left-radius: 6px;
    border-bottom-left-radius: 6px;
  }

  .school-selection-option:last-child{
    border-top-right-radius: 6px;
    border-bottom-right-radius: 6px;
  }

  #sidebar-filters{
    display: flex;
    align-items: flex-start;
    align-content: flex-start;
    justify-content: center;
    flex-direction: row;
    flex-wrap: wrap;
    height: 300px;
    font-family: ProximaNovaRegular;
    color: #1f3561;
  }

  #school-type-selection{
    height: 48px;
    align-items: center;
    justify-content: center;
    padding-top: 30px;
    width: fit-content;
    margin: 0 auto;
  }

  #filters-drilldown-wrapper{
    /* flex: 1 100%; */
    display: flex;
  }

  .school-type-selected {
    background-color: #78c6ea !important;
  }

  #school-level-wrapper {
    margin-left: 10px;
    margin-top: -8px;
  }

  #sort-dropdown{
    padding: 5px 40px 5px 23px;
    background-color: #e3e3e3;
    border-radius: 20px;
  }

  .school-results-item {
    height: fit-content;
    display: flex;
    justify-content: space-between;
    border-top: 1px solid grey;
    align-items: flex-start;
    flex-direction: row;
  }

  .school-results-item-left {
    align-items: flex-start;
    display: flex;
    flex-direction: column;
    height: fit-content;
    justify-content: space-between;
    flex-basis: 80%
  }

  .item-wrapper {
    width: 100%;
  }

  .hyperlink {
    color: blue;
    font-weight: 600;
  }

  .school-results-item-right {
    color: #808080;
    cursor: default !important;
    font-family: 'ProximaNovaRegular';
    height: fit-content;
    padding: 10px 10px 0 0;
  }

  @media only screen and (max-width : 1024px) {
    .school-results-item-right {
      font-size: 12px;
    }
     
  }

  .school-results-name {
    font-size: 18px;
    font-weight: 600px;
    color: #213763;
    padding: 10px 10px 10px 10px;
  }

  .school-info-wrapper{
    width: 70%;
    padding-bottom: 10px;
    float: left;
  }

  .school-results-element {
    padding-left: 10px;
    width: 70%;
    color: #213763;
    font-family: ProximaNovaRegular;
    font-size: 14px;
  }

  .school-icon-wrapper {
    margin: auto;
    float: right;
    margin-top: 10px;
    text-align: center;
  }

  .school-distance {
    font-weight: 600;
  }

  .school-results-expanded {
    font-family: ProximaNovaRegular;
    font-size: 14px;
    color: #213763;
    padding-left: 10px;
    margin-top: 10px;
  }

  .proxima-bold {
    font-family: ProximaNovaSemiBold;
  }
  .school-level-options {
    font-size: 16px;
    font-family: ProximaNovaRegular;
    color: #1f3561;
  }

  #school-type-wrapper {
    width: 100%;
    height: fit-content;
  }

  #sort-options-wrapper {
    margin-top: 15px;
    position: absolute;
    background-color: white;
    border-radius: 2px;
    box-shadow: 0 10px 20px 0 rgba(0, 0, 0, 0.09);
  }

  .sort-type {
    background-color: white;
    padding-left: 16px;
    padding-top: 8px;
    width: 200px;
    font-size: 18px;
    color: #1f3561;
    font-family: ProximaNovaRegular;
  }

  .sort-type:hover {
    background-color: #a4d8f1;
    cursor: pointer;
  }
  .arrow-down {
    border: solid #1f3561;
    border-width: 0 1.9px 1.9px 0;
    display: inline-block;
    padding: 3px;
    transform: rotate(45deg) translate(0px, -3px);
    -webkit-transform: rotate(45deg) translate(12px, -15px);
    margin-left: 10px;
  }

  #change-address-link {
    color: inherit;
  }

  .school-results-website-wrapper {
    width: 100%;
    text-align: center;
    margin-top: 10px;
    margin-bottom: 10px;
  }

  .website-button {
    border-radius: 20px;
    background-color: #cc7251;
    color: white;
    font-family: ProximaNovaRegular;
    font-size: 18px;
    padding: 9px 60px 9px 60px;
    width: fit-content;
    margin: 0 auto;
    cursor: pointer;
    box-shadow: 0 10px 20px 0 rgba(0, 0, 0, 0.30);
  }

  .compare-button-wrapper {
    width: 100%;
    text-align: center;
    margin-top: 10px;
    margin-bottom: 10px;
    font-family: ProximaNovaRegular;
    display: inline-block;
    cursor: pointer;
  }

  .compare-icon {
    width: 20px;
    height: 20px;
    transform: translate(0px, 5px);
  }

  #compare-wrapper {
    width: 101vw;
    height: 80px;
    box-shadow: 0 4px 10px 0 rgba(0, 0, 0, 0.09);
    background-color: #fafafa;
    position : absolute;
    bottom : -1vh;
    z-index: 100;
  }

  .compare-button {
    float: right;
    margin-right: 60px;
  }
  .final-compare {
    display: flex;
    align-items: center;
    padding: 9px 60px;
  }
  .final-compare-text {
    padding-left: 5px
  }

  .compare-item {
    line-height: 80px;
    vertical-align: middle;
    padding: 0px 34px 0px 34px;
    font-family: ProximaNovaSemiBold;
    font-size: 18px;
    color: #213763;
    border-right: 1px solid #E3E3E3;
    display: inline-block;
  }

  .footer-button-wrapper {
    display: inline-block;
    float: right;
    margin-top: 10px;
  }

  .compare-item-number {
    margin-left: 20px;
    background-color: #81c7e3;
    padding: 13px 12px 9px 12px;
    border-radius: 60px;
    cursor: pointer;
  }

  .delete-icon {
    width: 15px;
  }

  #compare-modal-wrapper {
    position: fixed;
    width: 70vw;
    height: 70vh;
    border-radius: 2px;
    -webkit-box-shadow: 0 10px 20px 0 rgba(0, 0, 0, 0.09);
    box-shadow: 0 10px 20px 0 rgba(0, 0, 0, 0.09);
    background-color: #ffffff;
    top: 9vh;
    left: 9vw;
    z-index: 100;
    overflow-y: scroll;
  }

  table {
    border-spacing: 0;
    border-collapse: collapse;
    width: 97%;
    margin-left: 1.5%;
    margin-right: 1.5%;
  }

  tr, th, td {
    padding: 5px 23.5px 5px 23.5px;
    border: solid 1px #cccccc;
  }

  th, td {
    font-family: ProximaNovaRegular;
    font-size: 14px;
    color: #213763;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    width: 100px;
  }

  td {
    font-family: ProximaNovaSemiBold;
    color: #213763;
  }

  /* Alternating table row color */
  tr:nth-child(even) {
    background-color: #fafafa;
  }

  .school-compare-header {
    padding-top: 1%;
    padding-bottom: 1%;
    text-align: center;
    font-size: 18px;
  }

  #compare-modal-header {
    font-family: ProximaNovaSemiBold;
    font-size: 18px;
    color: #213763;
    margin-left: 1.5%;
    padding-top: 1%;
    padding-bottom: 1%;
  }

  .compare-website-button {
    margin-top: 5px;
    margin-bottom: 5px;
    padding: 6px 30px 6px 30px;
    box-shadow: none;
  }

  .compare-delete-icon {
    float: right;
    margin-right: 15px;
    background-color: #e7e5e2;
    padding: 5px;
    border-radius: 20px;
    margin-top: -2px;
    cursor: pointer;
  }

  .no-results-found {
    width: 80%;
    margin: 0 auto;
  }

  .no-results-found-wrapper {
    border-radius: 6px;
    background-color: #fafafa;
    text-align: center;
    padding: 50px 10px 50px 10px;
    margin-top: 30px;
  }

  .no-results-found-text {
    font-family: ProximaNovaSemiBold;
    font-size: 20px;
    color: #1f3561;
  }

  .contact-us-button {
    margin-top: 20px;
    cursor: pointer;
  }

  .compare-button-list {
    background-color: #a4d8f1;
    border-radius: 25px;
    font-family: ProximaNovaRegular;
    cursor: pointer;
    padding: 5px 10px 5px 10px;
    margin: 0 auto;
    width: fit-content;
  }

  .school-icon-label {
      font-size: 12px;
  }

  /* start of tablet styles */
  @media screen and (max-width: 1200px) {
      #sort-dropdown {
          padding: 5px 23px 5px 5px;
          background-color: #e3e3e3;
          border-radius: 20px;
          font-size: 14px;
      }
      .arrow-down {
          transform: rotate(45deg) translate(8px,-10px);
      }
      #sort-wrapper {
          width: 100%;
      }
      #school-level-wrapper {
          margin-left: 0px;
          width: 70%;
      }
      .school-level-options {
          font-size: 14px;
      }
      #compare-modal-wrapper {
        top: 4vh;
        height: 95vh;
      }
      .compare-item {
        padding: 0px 14px 0px 14px;
      }
      th, td {
        font-size: 12px;
      }
      #sidebar-results {
          z-index: 100000;
          -webkit-overflow-scrolling:touch
      }
  }

  /* start of mobile styles */
  @media screen and (max-width: 440px) {
      #container {
          flex-flow: column;
        overflow-y: visible;
      }
      #sidebar {
          width: 100%;
          overflow-y: visible;
      }
      #map {
          display: none;
      }
      #sidebar-header {
        height: 320px;
        position: fixed;
        background-color: white;
        z-index: 10000000;
        margin-top: -30px;
      }
  }

  @media screen and (max-width: 322px) {
      .school-selection-option {
          font-size: 14px;
      }
  }


</style>
